/**
 * TabHeader Component
 *
 * Individual tab with features:
 * - Inline editing (double-click to rename)
 * - Character limit: 30 characters
 * - Active state highlighting
 * - Auto-focus on create for immediate rename
 * - Drag-and-drop support for reordering
 */

import { useState, useRef, useEffect } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { useDrag, useDrop } from 'react-dnd';
import { X } from 'lucide-react';
import { useSceneStore, type Tab } from '../../stores/sceneStore';
import { TabContextMenu } from './TabContextMenu';

interface TabHeaderProps {
  tab: Tab;
}

const DRAG_TYPE = 'TAB';

interface DragItem {
  tabId: string;
  order: number;
}

export function TabHeader({ tab }: TabHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(tab.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeTabId = useSceneStore((state) => state.activeTabId);
  const renameTab = useSceneStore((state) => state.renameTab);
  const deleteTab = useSceneStore((state) => state.deleteTab);
  const reorderTabs = useSceneStore((state) => state.reorderTabs);
  const tabs = useSceneStore((state) => state.tabs);

  const isActive = activeTabId === tab.id;
  const isNewTab = tab.name.startsWith('Scene ') && tabs.length === 1;

  // Auto-focus when tab is created
  useEffect(() => {
    if (isNewTab && isActive) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        setIsEditing(true);
      }, 100);
    }
  }, [isNewTab, isActive]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Drag and Drop
  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: (): DragItem => ({ tabId: tab.id, order: tab.order }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: (item: DragItem) => {
      if (item.tabId !== tab.id) {
        reorderTabs(item.tabId, tab.order);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleDoubleClick = () => {
    if (!isEditing) {
      setEditValue(tab.name);
      setIsEditing(true);
    }
  };

  const handleRename = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== tab.name) {
      renameTab(tab.id, trimmedValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setEditValue(tab.name);
      setIsEditing(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Enforce 30 character limit
    const value = e.target.value.substring(0, 30);
    setEditValue(value);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    deleteTab(tab.id);
  };

  return (
    <TabContextMenu tabId={tab.id}>
      <Tabs.Trigger
        value={tab.id}
        ref={(node) => {
          drag(node);
          drop(node);
        }}
        data-tab-id={tab.id}
        onDoubleClick={handleDoubleClick}
        className={`
          group relative flex shrink-0 items-center gap-2 rounded-t-md px-3 py-1.5 text-sm font-medium transition-colors
          ${
            isActive
              ? 'bg-neutral-800 text-neutral-100 border-b-2 border-blue-500'
              : 'text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-200 border-b-2 border-transparent'
          }
          ${isDragging ? 'opacity-50' : ''}
          ${isOver ? 'bg-neutral-700' : ''}
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900
        `}
        style={{ minWidth: '100px', maxWidth: '200px' }}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={handleChange}
            onBlur={handleRename}
            onKeyDown={handleKeyDown}
            className="w-full bg-neutral-700 px-1 text-neutral-100 outline-none"
            maxLength={30}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <span className="truncate">{tab.name}</span>
            {tabs.length > 1 && (
              <span
                onClick={handleClose}
                className="ml-auto hidden h-4 w-4 items-center justify-center rounded text-neutral-500 hover:bg-neutral-700 hover:text-neutral-100 group-hover:flex cursor-pointer"
                aria-label={`Close ${tab.name}`}
                role="button"
                tabIndex={-1}
              >
                <X className="h-3 w-3" />
              </span>
            )}
          </>
        )}
      </Tabs.Trigger>
    </TabContextMenu>
  );
}
